import { useCallback } from 'react';

import { useClient } from 'urql';

import { capitalizeFirstLetter } from '@sg/garnish';
import { type FlexPaymentMethod } from '@sg/graphql-schema';

import { useFeatureFlag } from '@order/LaunchDarkly';

import {
  FlexPaymentMethodDocument,
  type FlexPaymentMethodQuery,
} from './graphql/FlexPaymentMethod.generated';

// ────────────────────────────────────────────────────────────────────────────

/**
 * A hook for fetching the formatted flex payment method if available and enabled.
 */
export const useFetchFlexPaymentMethod = () => {
  const client = useClient();
  const isFlexEnabled = useFeatureFlag('CELS-5522-flex-enabled');

  // ─── Queries ──────────────────────────────────────────────────────────────

  const fetchFlexPaymentMethod = useCallback(async () => {
    if (!isFlexEnabled) {
      return undefined;
    }

    const query = client.query<FlexPaymentMethodQuery>;

    const response = await query(
      FlexPaymentMethodDocument,
      {},
      { requestPolicy: 'network-only' },
    ).toPromise();

    const result = response.data?.flexCustomerData;
    const hasFlexCustomer = result?.__typename === 'FlexCustomerData';
    const paymentMethods = hasFlexCustomer ? result.flexPaymentMethods : null;
    const paymentMethod = paymentMethods?.[0] ?? null; // only one payment method is supported.

    return paymentMethod ? formatFlexPaymentMethod(paymentMethod) : undefined;
  }, [isFlexEnabled, client.query]);

  // ──────────────────────────────────────────────────────────────────────────

  return { fetchFlexPaymentMethod };
};

// ─── Constants ───────────────────────────────────────────────────────────────

const HSA_FSA = 'HSA/FSA';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatFlexPaymentMethod(paymentMethod: FlexPaymentMethod) {
  if (!paymentMethod.brand || !paymentMethod.last4) return HSA_FSA;

  const brand = capitalizeFirstLetter(paymentMethod.brand.toLowerCase().replaceAll('_', ''));

  return `${HSA_FSA} ${brand} ${paymentMethod.last4}`;
}
